import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppRouteReuseStrategy } from './app-route-reuse-strategy';

export interface AppTab {
  path: string;      // base path — used for dedup, ROUTE_MAP lookup, active highlight
  fullPath: string;  // full URL — used for actual navigation (preserves :id params)
  title: string;
  icon: string;
}

const ROUTE_MAP: { [path: string]: { title: string; icon: string } } = {
  '/home':                        { title: 'Home',                   icon: 'home' },
  '/dashboard':                   { title: 'Dashboard',              icon: 'bar_chart' },
  '/employee-dashboard':          { title: 'Dashboard',              icon: 'bar_chart' },
  '/time-management':             { title: 'Time Management',        icon: 'schedule' },
  '/employee-attendance':         { title: 'Attendance',             icon: 'calendar_today' },
  '/schedules':                   { title: 'Schedule',               icon: 'event' },
  '/employee-management':         { title: 'Management',             icon: 'manage_accounts' },
  '/add-employee':                { title: 'Add Employee',           icon: 'person_add' },
  '/employee-details':            { title: 'Employee Details',       icon: 'person' },
  '/employee-schedule':           { title: 'Employee Schedule',      icon: 'event_note' },
  '/default-schedule':            { title: 'Schedule Range',         icon: 'schedule' },
  '/employee-workdays-requests':  { title: 'Change Schedule',        icon: 'schedule' },
  '/employee-leaves':             { title: 'Leaves',                 icon: 'send' },
  '/expense':                     { title: 'Reimbursement',          icon: 'account_balance_wallet' },
  '/employee-timeEntries':        { title: 'Biometric Verification', icon: 'fingerprint' },
  '/employee-devices':            { title: 'Devices',                icon: 'devices' },
  '/time-entries':                { title: 'Time Entries',           icon: 'schedule' },
  '/employee-timeSheets':         { title: 'Timesheet',              icon: 'pending_actions' },
  '/payroll-process':             { title: 'Payroll Process',        icon: 'payments' },
  '/payroll-movement':            { title: 'Payroll Movement',       icon: 'payments' },
  '/generate-payroll':            { title: 'Generate Payroll',       icon: 'settings' },
  '/generate-employee-fixed-timesheet': { title: 'Generate Employee Fixed Timesheet', icon: 'schedule' },
  '/employee-payroll':            { title: 'Employee Payroll',       icon: 'payments' },
  '/loans':                       { title: 'Loans',                  icon: 'account_balance' },
  '/deductions':                  { title: 'Deductions',             icon: 'tune' },
  '/payroll-adjustments':         { title: 'Adjustments',            icon: 'tune' },
  '/payroll-transactions':        { title: 'Payroll Transactions',   icon: 'account_tree' },
  '/support-ticket':              { title: 'Support Ticket',         icon: 'handyman' },
  '/broadcast-message':           { title: 'Broadcast Message',      icon: 'campaign' },
  '/personal-time-entries':       { title: 'Time Entries',           icon: 'pending_actions' },
  '/personal-timesheet':          { title: 'Timesheet',              icon: 'pending_actions' },
  '/personal-payroll':            { title: 'Payroll',                icon: 'payments' },
  '/client-registration':         { title: 'Client Registration',    icon: 'business' },
  '/access-management':           { title: 'Access Management',      icon: 'lock' },
  '/account-element':             { title: 'Account Element',        icon: 'settings' },
  '/payroll-charge':              { title: 'Charge',                 icon: 'settings' },
  '/payroll-department':          { title: 'Dep. / Branch / Job',    icon: 'settings' },
  '/holidays':                    { title: 'Holiday',                icon: 'celebration' },
  '/payroll-concept':             { title: 'Payroll Concept',        icon: 'settings' },
  '/payroll-definition':          { title: 'Payroll Definition',     icon: 'settings' },
  '/salary-scale':                { title: 'Salary Scale',           icon: 'stairs' },
  '/client-management':           { title: 'Clients',                icon: 'business' },
  '/system-config':               { title: 'System Config.',         icon: 'tune' },
  '/work-attendance-log':         { title: 'Work Attendance Log',    icon: 'event_note' },
  '/time-entries-summary':        { title: 'Time Entries Summary',   icon: 'access_time' },
  '/timesheet-summary':           { title: 'Timesheet Summary',      icon: 'pending_actions' },
  '/leave-report':                { title: 'Leave Summary',          icon: 'event_available' },
  '/payroll-summary':             { title: 'Payroll Summary',        icon: 'payments' },
  '/holidays-report':             { title: 'Holiday Summary',        icon: 'celebration' },
  '/late-report':                 { title: 'Late Report',            icon: 'schedule' },
  '/undertime-report':            { title: 'Undertime Report',       icon: 'timelapse' },
  '/sss-alphalist-report':        { title: 'SSS Alphalist',          icon: 'description' },
  '/philhealth-alphalist-report': { title: 'PhilHealth Alphalist',   icon: 'description' },
  '/pagibig-alphalist-report':    { title: 'Pag-IBIG Alphalist',     icon: 'description' },
  '/taxes-withheld-report':       { title: 'Taxes Withheld',         icon: 'receipt_long' },
  '/employee-dtr':                { title: 'Employee DTR',           icon: 'today' },
  '/bir-generation':              { title: 'BIR Generation',         icon: 'description' },
  '/workdays-requests':           { title: 'Change Schedule',        icon: 'schedule' },
  '/apply-leave':                 { title: 'Leave Form',             icon: 'send' },
  '/reimbursement-form':          { title: 'Reimbursement Form',     icon: 'payments' },
  '/loan-request':                { title: 'Loan Form',              icon: 'real_estate_agent' },
  '/process-time-entries':        { title: 'Time Entries Process',   icon: 'settings' },
  '/process-timesheet':           { title: 'Timesheet Process',      icon: 'settings' },
  '/process-payroll-invoice':     { title: 'Payroll Invoice',        icon: 'settings' },
  '/billing-summary':             { title: 'Billing Summary',        icon: 'receipt' },
  '/user-profile':                { title: 'Profile',                icon: 'person' },
  '/password-change':             { title: 'Change Password',        icon: 'lock' },
  '/settings':                    { title: 'Settings',               icon: 'settings' },
  '/invalid-timesheet':           { title: 'Invalid Timesheet',      icon: 'warning' },
  '/shift-planner':               { title: 'Manpower Planning',       icon: 'event_note' },
  '/batch-process-timesheet':     { title: 'Batch Process Timesheet', icon: 'dynamic_feed' },
  '/time-tracking-comparison':    { title: 'Time Tracking Comparison', icon: 'compare_arrows' },
};

@Injectable({ providedIn: 'root' })
export class TabService {

  private static readonly STORAGE_KEY = 'app_tabs';

  private _tabs = new BehaviorSubject<AppTab[]>([]);
  private _activeTab = new BehaviorSubject<string>('');

  tabs$ = this._tabs.asObservable();
  activeTab$ = this._activeTab.asObservable();

  constructor(private router: Router, private reuseStrategy: AppRouteReuseStrategy) {
    // Restore before subscribing — BehaviorSubject emits immediately on subscribe,
    // so restoring first means the first persisted value is the saved tabs (not []).
    this.restoreTabs();

    this._tabs.subscribe(tabs =>
      localStorage.setItem(TabService.STORAGE_KEY, JSON.stringify(tabs))
    );

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        const fullPath = e.urlAfterRedirects.split('?')[0];
        const path = '/' + fullPath.split('/')[1];
        this.activateOrAdd(path, fullPath);
      });
  }

  private restoreTabs(): void {
    try {
      const raw = localStorage.getItem(TabService.STORAGE_KEY);
      if (!raw) return;
      const tabs: AppTab[] = JSON.parse(raw);
      if (Array.isArray(tabs) && tabs.length > 0) {
        this._tabs.next(tabs);
      }
    } catch { /* ignore corrupt data */ }
  }

  private activateOrAdd(path: string, fullPath: string): void {
    const meta = ROUTE_MAP[path];
    if (!meta) return;

    const tabs = this._tabs.getValue();
    const existing = tabs.find(t => t.path === path);
    if (existing) {
      // Update fullPath so re-visiting with a different :id keeps navigation correct
      this._tabs.next(tabs.map(t => t.path === path ? { ...t, fullPath } : t));
    } else {
      this._tabs.next([...tabs, { path, fullPath, title: meta.title, icon: meta.icon }]);
    }
    this._activeTab.next(path);
  }

  closeTab(path: string): void {
    const tabs = this._tabs.getValue();
    const idx = tabs.findIndex(t => t.path === path);
    if (idx === -1) return;

    const next = tabs.filter(t => t.path !== path);
    this._tabs.next(next);
    this.reuseStrategy.clearCache(path);

    if (this._activeTab.getValue() === path) {
      // Mark BEFORE navigating so shouldDetach returns false and Angular destroys
      // the component instead of detaching it back into the cache.
      this.reuseStrategy.markClosing(path);
      const target = next[Math.max(0, idx - 1)];
      if (target) {
        this.router.navigateByUrl(target.fullPath);
      } else {
        this._activeTab.next('');
      }
    }
  }

  closeOthers(path: string): void {
    const tabs    = this._tabs.getValue();
    const active  = this._activeTab.getValue();
    tabs.filter(t => t.path !== path).forEach(t => {
      this.reuseStrategy.clearCache(t.path);
      if (t.path === active) this.reuseStrategy.markClosing(t.path);
    });
    const keep = tabs.filter(t => t.path === path);
    this._tabs.next(keep);
    this._activeTab.next(path);
    const target = keep[0];
    this.router.navigateByUrl(target?.fullPath ?? path);
  }

  closeToLeft(path: string): void {
    const tabs   = this._tabs.getValue();
    const idx    = tabs.findIndex(t => t.path === path);
    if (idx <= 0) return;
    const active = this._activeTab.getValue();
    tabs.slice(0, idx).forEach(t => {
      this.reuseStrategy.clearCache(t.path);
      if (t.path === active) this.reuseStrategy.markClosing(t.path);
    });
    const next = tabs.slice(idx);
    this._tabs.next(next);
    if (!next.find(t => t.path === active)) {
      this._activeTab.next(path);
      const target = next.find(t => t.path === path);
      this.router.navigateByUrl(target?.fullPath ?? path);
    }
  }

  closeAll(): void {
    const active = this._activeTab.getValue();
    if (active) this.reuseStrategy.markClosing(active);
    this._tabs.getValue().forEach(t => this.reuseStrategy.clearCache(t.path));
    this._tabs.next([]);
    this._activeTab.next('');
  }

  getTabsSnapshot(): AppTab[] {
    return this._tabs.getValue();
  }

  setTabs(tabs: AppTab[]): void {
    this._tabs.next(tabs);
  }
}
